"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const relatedServices = [{
  title: "Biohazard Cleanup",
  path: "/BiohazardCleanupPage",
  tagline: "Category 3 sanitation & trauma containment",
  description: "Certified decontamination and discreet cleanup of severe biological contaminants, viral pathogens, and hazardous site conditions.",
  specPill: "IICRC S540 Protocol"
}, {
  title: "Water Damage & Flood Restoration",
  path: "/WaterDamageFloodRestorationPage",
  tagline: "Rapid extraction & structural desiccant drying",
  description: "Industrial water extraction, subfloor drying, and infrared moisture verification to halt structural deterioration and secondary microbial growth.",
  specPill: "IICRC S500 Standard"
}];
const directBenefits = ["Local crews handling work directly without call centers", "Immediate 24/7 dispatch across MD, DC, and Northern Virginia", "Direct coordination with your insurance adjuster throughout", "Single contractor accountability from cleanup through rebuild"];
export default function WhyChooseTriserveRelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar / Overline */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-border/40 text-xs font-mono tracking-wider uppercase text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span>Direct Deployment Fleet • Maryland, DC, NoVA</span>
          </div>
          <span className="hidden sm:inline-block">Field Spec: Category 3 Sewage & Bio-Remediation</span>
        </div>

        {/* Narrative & Photo Block */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center pt-10 pb-16">
          {/* Narrative Content */}
          <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 rounded-md bg-secondary/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground w-fit mb-4 border border-secondary/50">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              <span>Dedicated Field Operations</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
              Direct local crews, from containment to complete drying.
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              When sewage backs up into a property, there is no margin for miscommunication. TriServe bypasses
              third-party dispatch centers entirely. The certified technicians who evaluate your property are the same
              local crew members handling the extraction, antimicrobial treatment, and drying phases directly.
            </p>

            <p className="mt-3 text-sm sm:text-base text-muted-foreground/90 leading-relaxed">
              Every stage is managed under one unified restoration protocol. We contain the hazard, extract contaminated
              wastewater with truck-mounted vacuum equipment, treat building assemblies with hospital-grade EPA
              disinfectants, and coordinate directly with your insurance adjuster so your claim progresses without
              unnecessary friction.
            </p>

            {/* Field Benefits Grid */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {directBenefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-1" />
                  <span className="text-xs sm:text-sm text-foreground/90 leading-snug">{benefit}</span>
                </div>)}
            </div>

            {/* Quick Dispatch CTA */}
            <div className="mt-8 pt-6 border-t border-border/40 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
                <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                  <PhoneCall className="h-4 w-4" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>
              <span className="text-xs font-mono text-muted-foreground">
                24/7 Live Crew Dispatch • No Call Centers
              </span>
            </div>
          </motion.div>

          {/* Documentary Field Photo */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-border/70 bg-card shadow-lg group">
              <div className="aspect-[4/3] sm:aspect-[16/11] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/technician_sewage_sanitizing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/technician_sewage_sanitizing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/technician_sewage_sanitizing.png-wvc-sizes" alt="TriServe technician sanitizing a basement floor with commercial decontamination gear" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.02]" />
              </div>

              {/* Equipment Spec Stamp Badge */}
              <div className="p-4 border-t border-border/70 bg-card/95 flex items-center justify-between text-xs">
                <div>
                  <p className="font-semibold text-foreground">Commercial Extraction & Antimicrobial Fogging</p>
                  <p className="text-muted-foreground text-[11px] font-mono mt-0.5">Truck-Mounted Direct Recovery System</p>
                </div>
                <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-mono uppercase bg-primary/20 text-primary-foreground font-bold">
                  Active On-Site
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Section Divider & Cross-Link Anchor */}
        <div className="pt-10 border-t border-border/50">
          <div className="max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
              Interconnected emergency services
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Sewage intrusions frequently require adjacent specialty remediation. We operate integrated units ready to deploy in parallel.
            </p>
          </div>

          {/* 2 Linked Service Cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedServices.map((service, i) => <motion.div key={service.title} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.1
          }}>
                <Card className="h-full bg-card border-border/70 hover:border-primary/60 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[11px] font-mono uppercase text-muted-foreground tracking-wider">
                        {service.specPill}
                      </span>
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium text-primary mt-1">
                      {service.tagline}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" className="w-full justify-between group/btn border-border/80 hover:bg-accent hover:text-accent-foreground text-sm font-semibold">
                      <Link to={service.path}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1 text-primary" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}